/* eslint-disable no-restricted-imports */
import { useMemo } from 'react';

import {
  SpidrState,
  type SecurityFocusFull,
  type Spidr,
  ConciergeTier,
} from '@rtkwlf/track-public';
import { NISTCategory } from '@rtkwlf/track-public/src/models/NISTCategory';
import { TechnicalDomain } from '@rtkwlf/track-public/src/models/TechnicalDomain';
import { Technology } from '@rtkwlf/track-public/src/models/Technology';
import type { TFunction } from 'i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Box, Typography } from '@packages/fenrir/components';
import { Button } from '@packages/fenrir/components';
import {
  DataGridPro,
  GridSelectOperator,
  type GridColDef,
  type GridRenderCellParams,
  getFilteredGridSelectOperators,
  GridToolbar,
} from '@packages/fenrir/components/datagrid';
import { Stack } from '@packages/fenrir/components/stack';

import { ConciergeTierBadge } from '../../../../components/concierge-tier-badge/concierge-tier-badge';
import type { SpidrStateBadgeProps } from '../../../../components/spidr-state-badge';
import { SpidrStateBadge } from '../../../../components/spidr-state-badge';
import { createOptions } from '../../../../components/tables/_components/create-options';
import { getHaveAnyOfFilterOperator } from '../../../../components/tables/_components/get-have-any-of-filter-operator';
import { ListCell } from '../../../../components/tables/_components/list-cell';
import { outcomeExpirationColumnFilterOperator } from '../../../../components/tables/_components/outcome-expiration-column-filter-operator';
import { TechnicalDomainsListCell } from '../../../../components/tables/_components/technical-domains-list-cell';
import { routes } from '../../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../../locales/use-security-journey-translations';
import { getDaysAgo } from '../../../../utils/get-days-ago';
import { getDaysToExpiry } from '../../../../utils/get-days-to-expiry';
import { sortOutcomeExpirationColumn } from '../../../../utils/sort-outcome-expiration-column';

/* -------------------------------------------------------------------------------------------------
 * Utilities
 * -----------------------------------------------------------------------------------------------*/
type SpidrRow = {
  id: string;
  name: string;
  bpId: string;
  state: string;
  tier?: ConciergeTier;
  nist?: string[];
  technicalDomains?: string[];
  delivered?: Date | null;
  doesBlueprintExpire: boolean;
  nullableExpiryDate: Date | null;
  technologies?: string[];
};

type ParseSpidrsForTableParams = {
  /**
   * spidrs to be transformed
   */
  spidrs: Spidr[];
};

/**
 * filter for Expiration
 */
export const ExpirationFilterOption = {
  DAYS_45: 'Expiring in < 45 days',
  DAYS_30: 'Expiring in < 30 days',
  DAYS_15: 'Expiring in < 15 days',
  EXPIRED: 'Expired',
} as const;

/**
 * Parses spidrs into the type that is required
 * for the spidrs table
 *
 * @param params of type `ParseSpidrsForTableParams`
 */
const parseSpidrsForTable = (params: ParseSpidrsForTableParams): SpidrRow[] => {
  if (params.spidrs.length === 0) return [];
  const rows = params.spidrs.map((spidr): SpidrRow => {
    const blueprintTechnicalDomains = new Set([
      spidr.blueprint.primaryTechnicalDomain,
    ]);

    if (spidr.blueprint.technicalDomains) {
      spidr.blueprint.technicalDomains.forEach((technicalDomain) =>
        blueprintTechnicalDomains.add(technicalDomain),
      );
    }

    return {
      id: spidr.id,
      bpId: spidr.blueprint.id,
      name: spidr.blueprint.name,
      state: spidr.state,
      tier: spidr.blueprint.tier,
      nist: spidr.blueprint.nist,
      delivered: spidr.deliveredAt,
      nullableExpiryDate: spidr.expiryDate,
      doesBlueprintExpire: spidr.blueprint.doesExpire,
      technicalDomains: Array.from(blueprintTechnicalDomains),
      technologies: spidr.blueprint.technologies,
    };
  });
  return rows;
};
/* -------------------------------------------------------------------------------------------------
 * ActionColumn
 * -----------------------------------------------------------------------------------------------*/
const ActionColumn: React.FC<{
  spidrID: string;
  spidrState: string;
  focusID: string;
}> = ({ spidrID, spidrState, focusID }) => {
  const { t } = useSecurityJourneyTranslations();
  const isSpidrViewable =
    spidrState === SpidrState.Delivered ||
    spidrState === SpidrState.Complete ||
    spidrState === SpidrState.Legacy;

  if (!isSpidrViewable) {
    return null;
  }
  return (
    <Button
      component={RouterLink}
      to={routes.securityFocuses.focusID.spidrs.spidrID.index({
        spidrID,
        focusID,
      })}
      data-testid={`view-spidr-button-${spidrID}`}
    >
      {t('focusID.securityFocusSpidrsTable.actions.viewSpidr')}
    </Button>
  );
};

/* -------------------------------------------------------------------------------------------------
 * ExpirationColumn
 * -----------------------------------------------------------------------------------------------*/
const ExpirationColumn: React.FC<{
  spidrState: string;
  doesBlueprintExpire: boolean;
  spidrExpiryDate: Date | null;
}> = ({ spidrState, doesBlueprintExpire, spidrExpiryDate }) => {
  const { t } = useSecurityJourneyTranslations();

  const deliveredOrCompleteStates: SpidrState[] = [
    SpidrState.Delivered,
    SpidrState.Complete,
  ];

  const isDeliveredOrComplete = deliveredOrCompleteStates.includes(
    spidrState as SpidrState,
  );

  const outcomeExpiration = useMemo(() => {
    if (!isDeliveredOrComplete && doesBlueprintExpire) {
      return t('focusID.securityFocusSpidrsTable.expirationColumn.tbd');
    } else if (
      isDeliveredOrComplete &&
      doesBlueprintExpire &&
      spidrExpiryDate
    ) {
      return getDaysToExpiry(spidrExpiryDate);
    } else {
      return t('focusID.securityFocusSpidrsTable.expirationColumn.noExpiry');
    }
  }, [isDeliveredOrComplete, doesBlueprintExpire, spidrExpiryDate, t]);

  return <div>{outcomeExpiration}</div>;
};
/* -------------------------------------------------------------------------------------------------
 * Columns
 * -----------------------------------------------------------------------------------------------*/
const SpidrsTableColumn = {
  Name: 'name',
  State: 'state',
  Tier: 'tier',
  TechnicalDomains: 'technicalDomains',
  Technologies: 'technologies',
  Nist: 'nist',
  Delivered: 'delivered',
  OutcomeExpiration: 'outcomeExpiration',
  Actions: 'actions',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
type SpidrsTableColumn =
  (typeof SpidrsTableColumn)[keyof typeof SpidrsTableColumn];

type ColumnDefinition = GridColDef<SpidrRow>[];

type CustomCellProps = GridRenderCellParams<SpidrRow, unknown> & {};

type GetColumnsOptions = {
  t: TFunction<'translation', undefined>;
  focus: SecurityFocusFull;
};

const getColumns = ({ t, focus }: GetColumnsOptions): ColumnDefinition => {
  const defaultColumns: GridColDef<SpidrRow>[] = [
    {
      field: SpidrsTableColumn.Name,
      headerName: t('focusID.securityFocusSpidrsTable.headers.name'),
      minWidth: 300,
      resizable: true,
      display: 'flex',
    },
    {
      field: SpidrsTableColumn.State,
      headerName: t('focusID.securityFocusSpidrsTable.headers.status'),
      width: 200,
      resizable: false,
      renderCell: ({ row }) => (
        <Stack flexDirection="column" width="100%" alignItems="flex-start">
          <SpidrStateBadge state={row.state as SpidrStateBadgeProps['state']} />
        </Stack>
      ),
      type: 'singleSelect',
      valueOptions: createOptions(Object.values(SpidrState)),
      filterOperators: getFilteredGridSelectOperators([
        GridSelectOperator.isAnyOf,
      ]),
    },
    {
      field: SpidrsTableColumn.Tier,
      headerName: t('focusID.securityFocusSpidrsTable.headers.tier'),
      width: 150,
      resizable: false,
      renderCell: ({ row }) => (
        <ConciergeTierBadge.Spidr tier={row.tier ? row.tier : '1'} />
      ),
      type: 'singleSelect',
      valueOptions: [
        { value: ConciergeTier._1, label: 'Silver' },
        { value: ConciergeTier._2, label: 'Gold' },
        { value: ConciergeTier._3, label: 'Platinum' },
      ],
      filterOperators: getFilteredGridSelectOperators([
        GridSelectOperator.isAnyOf,
      ]),
    },
    {
      field: SpidrsTableColumn.TechnicalDomains,
      headerName: t(
        'focusID.securityFocusSpidrsTable.headers.technicalDomains',
      ),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: ({ row }) => {
        return (
          <TechnicalDomainsListCell technicalDomains={row.technicalDomains} />
        );
      },
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(TechnicalDomain)),
      ),
    },
    {
      field: SpidrsTableColumn.Technologies,
      headerName: t('focusID.securityFocusSpidrsTable.headers.technologies'),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: ({ row }) => {
        return <ListCell value={row.nist} />;
      },
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(Technology)),
      ),
    },
    {
      field: SpidrsTableColumn.Nist,
      headerName: t('focusID.securityFocusSpidrsTable.headers.nist'),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: ({ row }) => <ListCell value={row.nist} />,
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(NISTCategory)),
      ),
    },

    {
      field: SpidrsTableColumn.Delivered,
      headerName: t('focusID.securityFocusSpidrsTable.headers.deliveredAt'),
      width: 300,
      renderCell: ({ row }) => {
        if (row.delivered) {
          return getDaysAgo(row.delivered, t('common.spidrStates.delivered'));
        }
        return null;
      },
    },
    {
      field: SpidrsTableColumn.OutcomeExpiration,
      headerName: t(
        'focusID.securityFocusSpidrsTable.headers.outcomeExpiration',
      ),
      width: 300,
      sortable: true,
      valueGetter: (value, row) => row,
      renderCell: ({ row }) => {
        return (
          <ExpirationColumn
            spidrState={row.state}
            doesBlueprintExpire={row.doesBlueprintExpire}
            spidrExpiryDate={row.nullableExpiryDate}
          />
        );
      },
      sortComparator: (v1, v2) => {
        return sortOutcomeExpirationColumn(v1, v2);
      },
      filterOperators: outcomeExpirationColumnFilterOperator(
        createOptions(Object.values(ExpirationFilterOption)),
      ),
    },

    {
      field: SpidrsTableColumn.Actions,
      type: 'actions',
      align: 'right',
      width: 150,
      resizable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <ActionColumn
            focusID={focus.id}
            spidrID={row.id}
            spidrState={row.state}
          />
        );
      },
    },
  ] satisfies ColumnDefinition;

  return defaultColumns;
};

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusSpidrsTableTable
 * -----------------------------------------------------------------------------------------------*/
type SecurityFocusSpidrsTableProps = {
  /**
   * security focus
   */
  focus: SecurityFocusFull;

  /**
   * security focus
   */
  isOrganizationTierNonLegacy?: boolean | null;
};

const SecurityFocusSpidrsTable = (
  tableProps: SecurityFocusSpidrsTableProps,
) => {
  const { focus, isOrganizationTierNonLegacy } = tableProps;
  /**
   * get an instance of the translations
   */
  const { t } = useSecurityJourneyTranslations();

  const spidrs = parseSpidrsForTable({
    spidrs: focus?.spidrs ?? [],
  });

  /**
   * compute the columns for the table
   */
  const columns = useMemo(() => {
    const tempColumns = getColumns({
      t,
      focus,
    });
    const filteredColumns = isOrganizationTierNonLegacy
      ? tempColumns
      : tempColumns.filter((col) => col.field !== SpidrsTableColumn.Tier);
    return filteredColumns;
  }, [t, focus, isOrganizationTierNonLegacy]);

  const slots = {
    toolbar: () => (
      <GridToolbar
        showQuickFilter
        quickFilterProps={{
          placeholder: t(
            'common.blueprintsTable.filters.quickFilter.placeholder',
          ),
        }}
      />
    ),
    noRowsOverlay: () => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body1">
          {t('focusID.securityFocusSpidrsTable.noResultMessage.description')}
        </Typography>
      </Box>
    ),
  };

  return (
    <Stack spacing={3} width="100%" data-testid="security-focus-spidrs-table">
      <Typography variant="h3">
        {t('focusID.securityFocusSpidrsTable.title')}
      </Typography>
      <DataGridPro
        rows={spidrs}
        columns={columns}
        getRowId={(row: SpidrRow) => row.id}
        slots={slots}
        hideFooter
        disableRowSelectionOnClick
        disableDensitySelector
        disableChildrenFiltering
        getRowHeight={() => 'auto'}
        initialState={{
          pinnedColumns: {
            right: ['actions'],
          },
        }}
      />
    </Stack>
  );
};

export { SecurityFocusSpidrsTable, parseSpidrsForTable, SpidrsTableColumn };

export type { SecurityFocusSpidrsTableProps, SpidrRow, CustomCellProps };
